import { useState } from 'react';
import { Link, useCanGoBack, useNavigate, useParams, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Check, Copy, Pencil, ShoppingBasket, Sparkles, Trash2, type LucideIcon } from 'lucide-react';
import { Button, CenteredSpinner, Chip, ErrorState } from '@/components/ui';
import { DownloadPanel } from '@/components/DownloadPanel';
import { useToast } from '@/components/Toasts';
import { KIND_ICON, KIND_TINT, absoluteUrl, canDisplayRaw, cn, formatFileSize, tagText } from '@/lib/format';
import { t } from '@/lib/i18n';
import {
  useAddToBasket,
  useAsset,
  useBasket,
  useDeleteAssets,
  useHealth,
  useRemoveFromBasket,
  useRestore,
  useTagNow,
  useUpdateMetadata,
} from '@/lib/queries';

type TagFields = {
  brand: string;
  productTitle: string;
  imageType: string;
  description: string;
  /** Space-separated "#tag #tag" — how people actually type hashtags. */
  hashtags: string;
};

/**
 * How tall the preview may render, as a CSS length.
 *
 * The panel is wide, so an image told to fill it scales up — good for the 1500px
 * pack shots, bad for a 250px thumbnail stretched to 70vh, which is just a
 * blurry version of itself. This caps the upscale at 2x the natural height
 * while never exceeding the 70vh the layout allows.
 *
 * 2x is the point where a typical pack shot still reads cleanly on a HiDPI
 * screen (where it is 1:1 in device pixels) without looking soft on a standard
 * one. Unknown dimensions fall back to 70vh: Pimcore leaves width/height null
 * for some images, and clamping those to a guess would shrink files that are
 * actually large.
 */
function imageDisplayCap(width: number | null, height: number | null): string {
  if (!height || !width) return "calc(100vh - 12rem)";
  return `min(calc(100vh - 12rem), ${height * 2}px)`;
}

/**
 * Asset detail — preview + metadata + the AI-tag review panel.
 *
 * The tag panel shows the five AI-generated fields (brand / product title /
 * image type / description / hashtags) and lets a user correct them.
 *
 * There is deliberately no bare "Confirm" action. Tags arrive already applied,
 * so confirming changed nothing a user could see, and in drop-in mode it hit
 * Pimcore's read-only guard and silently failed. The useful action is editing:
 * fix a wrong field or add a missing hashtag.
 */
export function AssetDetailPage() {
  const { assetId } = useParams({ from: '/shell/assets/$assetId' });
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const { data: asset, isPending, error, refetch } = useAsset(assetId);
  const tagNow = useTagNow(assetId);
  const saveTags = useUpdateMetadata(assetId);
  const [editing, setEditing] = useState(false);
  const { data: health } = useHealth();
  const readOnly = health?.readOnly ?? false;
  // aiTags aren't on the base asset endpoint; fetch them alongside via the tag route result / re-fetch.
  const [localTags, setLocalTags] = useState<TagFields | null>(null);

  if (isPending) return <CenteredSpinner label={t('assetDetailPage.loadingAsset')} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!asset) return null;

  const Icon = KIND_ICON[asset.kind];
  const meta = asset.metadata ?? {};
  const tags: TagFields = localTags ?? {
    brand: (meta['brand'] as string) ?? '',
    productTitle: (meta['title'] as string) ?? '',
    imageType: (meta['imageType'] as string) ?? '',
    description: (meta['description'] as string) ?? '',
    hashtags: Array.isArray(meta['tags']) ? (meta['tags'] as string[]).map((t) => `#${t}`).join(' ') : '',
  };
  // Placeholder-aware: an asset whose only "tags" are the tagger's "{None}"
  // strings is untagged, and should offer "Tag now" rather than an empty panel.
  const hasTags = Boolean(
    tagText(tags.brand) || tagText(tags.productTitle) || tagText(tags.description) || tags.hashtags,
  );

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="w-full px-6 py-5">
        {/* Back retraces history when there is any, so it returns to the page
            you actually came from *with its state intact* — the artwork index
            still filtered to the code you searched, a search still showing its
            results. Sending it to the asset's folder instead was wrong for
            every entry point except the folder browser: opening a PDF from the
            artwork index and clicking Back dumped people in an unrelated
            folder listing, with the lookup they had typed gone.

            The folder link stays as the fallback for loads with no history to
            go back to — a pasted link, a new tab, a bookmark. Assets outside
            any folder (and the odd null folderId Pimcore returns) fall back to
            the root, the only sensible destination for them. */}
        {canGoBack ? (
          <button
            onClick={() => router.history.back()}
            className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted hover:text-strong"
          >
            <ArrowLeft className="size-3.5" /> {t('assetDetailPage.back')}
          </button>
        ) : asset.folderId != null ? (
          <Link
            to="/folders/$folderId"
            params={{ folderId: asset.folderId }}
            className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted hover:text-strong"
          >
            <ArrowLeft className="size-3.5" /> {t('assetDetailPage.back')}
          </Link>
        ) : (
          <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted hover:text-strong">
            <ArrowLeft className="size-3.5" /> {t('assetDetailPage.back')}
          </Link>
        )}

        {/* Identity spans the full page width, above both columns, so the
            preview and the download/tags column start on the same line. It
            lived in the details column (one line of text beside a 70vh image,
            leaving the space next to the lower half of the artwork blank), and
            then in the left column, which pushed the right column's top edge
            below the image's. Neither aligned the two columns; this does. */}
        {/* Identity text left, facts and action right, on the same rows: the
            chips and basket button had a third row to themselves while the
            space beside the URL and library path sat empty. Wraps back to a
            stack below sm, where two groups side by side would squeeze both. */}
        <div className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0 flex-1">
            {/* break-all, not break-words: these filenames are one long
                underscore-joined token with no break opportunity, so break-words
                leaves them overflowing. */}
            {/* text-2xl: sized for a third-width column before, where a long
                filename wrapped to three lines. Across the full page it fits on
                one, so it can carry the weight of a page title. */}
            <h1 className="break-all text-2xl font-semibold text-strong">{asset.fileName}</h1>
            <AssetLink url={absoluteUrl(`/assets/${asset.id}`)} libraryPath={asset.path} />
          </div>
          {/* shrink-0 so the chips keep their own line rather than being
              compressed by a long filename; justify-end keeps them and the
              button flush right against the header's edge. */}
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <Chip size="md">{t(`assetKind.${asset.kind}`)}</Chip>
            <Chip size="md">{formatFileSize(asset.sizeBytes)}</Chip>
            {asset.width && asset.height ? <Chip size="md">{asset.width}×{asset.height}</Chip> : null}
            <span className="ml-1 flex items-center gap-2">
              <BasketButton assetId={asset.id} />
              <DeleteButton assetId={asset.id} fileName={asset.fileName} folderId={asset.folderId} />
            </span>
          </div>
        </div>

        {/* items-start: grid items stretch to the tallest row by default, which
            let the preview box grow with the metadata column and sink the
            artwork down the page. Each column now sizes to its own content. */}
        {/* minmax(0,…) on both tracks: an `fr` track is min-content-sized by
            default, so the long unbroken filenames in the details column blew
            it out to 891px and squeezed the preview to 144px. Zero minimums let
            the ratio actually hold and let the text wrap instead. */}
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          {/* Preview — sticky so the artwork stays visible while the details
              column scrolls past it on tall pages. */}
          {/* items-stretch so a document preview can fill the panel width; the
              inner content centres itself. */}
          <div className="flex items-stretch justify-center rounded-[var(--radius-card)] surface-sunken border line p-4 lg:sticky lg:top-5" style={{ minHeight: 320 }}>
            {/* Gate on the MIME, not just the kind: Pimcore files Illustrator
                artwork as kind='image' with an application/pdf mimetype, and an
                <img> pointed at those raw bytes renders as a broken image.
                Those fall through to the rendered preview below. */}
            {asset.kind === 'image' && canDisplayRaw(asset.mime) ? (
              // w-full, not max-w-full: `max-w` caps at the image's natural
              // size, so a 250x250 pack shot rendered at 250px in a panel three
              // times that wide. Letting it grow fills the space it was given.
              //
              // The upscale is bounded by `imageDisplayCap`, because a small
              // source blown up to 70vh is just a blurry version of itself —
              // and by object-contain, which never distorts the aspect ratio.
              <img
                src={asset.url}
                alt={asset.fileName}
                style={{ maxHeight: imageDisplayCap(asset.width, asset.height) }}
                className="m-auto w-full rounded object-contain"
              />
            ) : asset.kind === 'video' ? (
              <video src={asset.url} controls className="m-auto max-h-[70vh] max-w-full rounded" />
            ) : asset.thumbUrl ? (
              // Documents (PDF/AI/EPS/PSD) render a first-page preview. It is
              // still a link to the original: the preview is one page, so the
              // way to read the rest is to open the file.
              <DocumentPreview thumbUrl={asset.thumbUrl} url={asset.url} fileName={asset.fileName} icon={Icon} tint={KIND_TINT[asset.kind]} />
            ) : (
              <div className="m-auto flex flex-col items-center gap-3 py-10">
                <Icon className={cn('size-16', KIND_TINT[asset.kind])} />
                <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-500 hover:underline">{t('assetDetailPage.openFile')}</a>
              </div>
            )}
          </div>

          {/* Details + AI tags */}
          <div className="space-y-5">
            <DownloadPanel assetId={asset.id} fileName={asset.fileName} />

            {/* AI tag panel */}
            <div className="rounded-[var(--radius-card)] surface-card border line p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                  <Sparkles className="size-3.5 text-brand-500" /> {t('assetDetailPage.aiTags')}
                </h2>
                {/* Edit sits beside Re-tag rather than under the hashtags: as a
                    footer row it cost a full row plus its own caption line at
                    the bottom of the longest panel on the page. Only while
                    there are tags and we are not already editing — the editor
                    carries its own Cancel, and there is nothing to edit before
                    the first tagging run. */}
                <div className="flex items-center gap-2">
                {hasTags && !editing && (
                  // The read-only caption that used to sit beside this button
                  // moved onto the button's tooltip. The editor still says it
                  // in full and disables Save, so opening it read-only is
                  // informative rather than a dead end — the fields are worth
                  // reading even when they cannot be changed.
                  <Button
                    variant="secondary"
                    className="h-7 text-xs"
                    title={readOnly ? t('assetDetailPage.readOnlyTooltip') : undefined}
                    onClick={() => setEditing(true)}
                  >
                    <Pencil className="size-3.5" /> {t('assetDetailPage.edit')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  className="h-7 text-xs"
                  loading={tagNow.isPending}
                  onClick={() =>
                    tagNow.mutate(undefined, {
                      onSuccess: (r) => {
                        // The tagger returns raw AI field names; normalise onto
                        // TagFields so the panel and editor agree on shape.
                        const t = (r as { tags: Record<string, unknown> }).tags ?? {};
                        const str = (v: unknown) => (typeof v === 'string' ? v : '');
                        setLocalTags({
                          brand: str(t.brand),
                          productTitle: str(t.productTitle ?? t.title),
                          imageType: str(t.imageType),
                          description: str(t.description),
                          hashtags: Array.isArray(t.hashtags)
                            ? (t.hashtags as string[]).map((h) => `#${String(h).replace(/^#/, '')}`).join(' ')
                            : str(t.hashtags),
                        });
                      },
                    })
                  }
                >
                  {hasTags ? t('assetDetailPage.reTag') : t('assetDetailPage.tagNow')}
                </Button>
                </div>
              </div>

              {hasTags ? (
                editing ? (
                  <TagEditor
                    tags={tags}
                    readOnly={readOnly}
                    saving={saveTags.isPending}
                    error={saveTags.error as Error | null}
                    onCancel={() => { setEditing(false); saveTags.reset(); }}
                    onSave={(next) => {
                      setLocalTags(next);
                      saveTags.mutate(
                        {
                          brand: next.brand,
                          title: next.productTitle,
                          imageType: next.imageType,
                          description: next.description,
                          // Stored as an array; the editor works in "#tag #tag" text.
                          tags: next.hashtags.split(/\s+/).map((t) => t.replace(/^#/, '')).filter(Boolean),
                        },
                        { onSuccess: () => setEditing(false) },
                      );
                    }}
                  />
                ) : (
                // One aligned stack rather than a 2-col grid for brand and
                // image type: with labels now inline, every field is a single
                // compact row, and a shared label column reads straighter than
                // two fields side by side on their own row. Searching
                // brand/image type finds sibling assets; a near-unique title or
                // prose does not, so only those two link out.
                <dl className="space-y-1 text-sm">
                  <Field label={t('assetDetailPage.brand')} value={tags.brand} searchKey="brand" />
                  <Field label={t('assetDetailPage.imageType')} value={tags.imageType} searchKey="imageType" />
                  <Field label={t('assetDetailPage.product')} value={tags.productTitle} />
                  <Field label={t('assetDetailPage.description')} value={tags.description} small />
                  {tags.hashtags && (
                    // Same label column as the fields above, so the chips start
                    // on the values' left edge instead of a row of their own.
                    <div className="flex gap-3 pt-0.5">
                      <dt className="w-[5.5rem] shrink-0 text-[11px] leading-5 text-faint">{t('assetDetailPage.hashtags')}</dt>
                      <dd className="min-w-0 flex-1">
                        <HashtagList
                          tags={tags.hashtags
                            .split(/\s+/)
                            // Drop placeholder tags the same way the fields do —
                            // "#None" is not worth a chip or a search link.
                            .filter((h) => tagText(h.replace(/^#/, '')))}
                        />
                      </dd>
                    </div>
                  )}
                </dl>
                )
              ) : (
                <p className="text-sm text-faint">{t('assetDetailPage.notTaggedYet')}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * First-page preview for a document, linking through to the original.
 *
 * `hasThumbnail` is a promise the server makes from the MIME type alone, before
 * anything is rendered — an encrypted or damaged PDF still advertises a preview
 * and then fails to produce one. So a broken image here is expected, not
 * exceptional, and falls back to the same kind icon non-previewable files get.
 */
function DocumentPreview({
  thumbUrl,
  url,
  fileName,
  icon: Icon,
  tint,
}: {
  thumbUrl: string;
  url: string;
  fileName: string;
  icon: LucideIcon;
  tint: string;
}) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="m-auto flex flex-col items-center gap-3 py-10">
        <Icon className={cn('size-16', tint)} />
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-500 hover:underline">{t('assetDetailPage.openFile')}</a>
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="group flex w-full flex-col items-center gap-3">
      {/* w-full so the preview fills the panel like an image asset does, rather
          than sitting small at its natural 400px thumbnail width. */}
      <img
        src={thumbUrl}
        alt={t('assetDetailPage.firstPageOf', { fileName })}
        onError={() => setFailed(true)}
        className="max-h-[70vh] w-full rounded border line bg-white object-contain shadow-sm"
      />
      <span className="text-sm text-brand-500 group-hover:underline">{t('assetDetailPage.openFile')}</span>
    </a>
  );
}

/**
 * The asset's shareable address, plus its path inside the library.
 *
 * Two different things, so both are shown. The URL points at this page
 * (`/assets/:id`) — the thing a colleague can actually open. Deliberately NOT
 * `/api/assets/:id/raw`: that serves bytes, not a page, and drops the recipient
 * into a bare image with no metadata or navigation.
 *
 * It is absolute so it resolves on whichever host is serving (devdam / dam /
 * localhost); a bare "/PRODUCT_IMAGES/…" is Pimcore's internal tree, not a route
 * this app serves, so pasting it anywhere would 404. The library path stays
 * visible underneath because it's what people quote to each other and what
 * matches Pimcore's own tree.
 */
function AssetLink({ url, libraryPath }: { url: string; libraryPath: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex items-start gap-1.5">
        {/* Not target="_blank": this is the page you're already on, so it's here
            to be read and copied rather than followed. */}
        {/* text-sm, up from text-xs: this is the address people copy and read
            back to each other, and the header now has the page width to spend
            on it. */}
        <a href={url} className="break-all text-sm text-brand-500 hover:underline">
          {url}
        </a>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="shrink-0 rounded p-0.5 text-faint transition-colors hover:text-strong"
          aria-label={copied ? t('assetDetailPage.linkCopied') : t('assetDetailPage.copyLink')}
          title={copied ? t('assetDetailPage.copied') : t('assetDetailPage.copyLink')}
        >
          {copied ? <Check className="size-4 text-brand-500" /> : <Copy className="size-4" />}
        </button>
      </div>
      <p className="break-all text-sm text-faint">{libraryPath}</p>
    </div>
  );
}

/**
 * Add/remove this asset from the share basket.
 *
 * Deliberately a toggle rather than a one-way "add": the old Pimcore basket
 * gave no indication that an asset was already in it, so people added the same
 * file repeatedly and only found out on the share page.
 */
/**
 * Delete this asset, with an undo on the toast.
 *
 * Navigates back to the containing folder on success: this page is a view of
 * one asset, and staying on it after the asset is gone would show an error
 * state for something the user did deliberately. The undo restores it at the
 * same id, so the link back into it stays valid.
 *
 * Not gated on isAdmin: Pimcore's `delete` grant is per-folder and independent
 * of the admin flag, so an admin-only button would hide the action from users
 * who hold the right. The server enforces it and a refusal surfaces as an error
 * toast.
 */
function DeleteButton({
  assetId,
  fileName,
  folderId,
}: {
  assetId: number;
  fileName: string;
  folderId: number | null;
}) {
  const del = useDeleteAssets();
  const restore = useRestore();
  const toast = useToast();
  const navigate = useNavigate();
  return (
    <Button
      variant="secondary"
      className="h-8 px-3 text-xs text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-950/50"
      loading={del.isPending}
      onClick={() => {
        if (!window.confirm(t('assetDetailPage.deleteConfirm', { fileName }))) return;
        del.mutate([assetId], {
          onSuccess: (r) => {
            if (r.failed.length) {
              toast.push({ message: t('assetDetailPage.couldNotDelete', { fileName }), tone: 'error' });
              return;
            }
            toast.push({
              message: t('assetDetailPage.deleted', { fileName }),
              onUndo: r.handles.length
                ? async () => {
                    await restore.mutateAsync(r.handles);
                  }
                : undefined,
            });
            void navigate({ to: folderId ? '/folders/$folderId' : '/', ...(folderId ? { params: { folderId: String(folderId) } } : {}) } as never);
          },
        });
      }}
    >
      <Trash2 className="size-3.5" /> {t('assetDetailPage.delete')}
    </Button>
  );
}

function BasketButton({ assetId }: { assetId: number }) {
  const { data: basket } = useBasket();
  const add = useAddToBasket();
  const remove = useRemoveFromBasket();
  const inBasket = basket?.some((b) => b.assetId === assetId) ?? false;

  return (
    <span className="flex items-center gap-2">
      <Button
        variant={inBasket ? 'secondary' : 'primary'}
        // Sits on the chip row, so it tracks the chips' weight. h-8/text-xs
        // now the row is a full-width page header rather than a narrow column.
        className="h-8 px-3 text-xs"
        loading={add.isPending || remove.isPending}
        onClick={() => (inBasket ? remove.mutate(assetId) : add.mutate({ assetId }))}
      >
        <ShoppingBasket className="size-3.5" />
        {/* Shortened once in the basket: "In basket — remove" is a wide label
            for a row that also carries the chips and the link below. */}
        {inBasket ? t('assetDetailPage.remove') : t('assetDetailPage.addToBasket')}
      </Button>
      {inBasket && (
        <Link to="/basket" className="whitespace-nowrap text-xs text-brand-500 hover:underline">
          {t('assetDetailPage.goToBasket')}
        </Link>
      )}
    </span>
  );
}

/**
 * One metadata row. Passing `searchKey` makes the value a link that runs an AI
 * search on that field in a new tab — the same behaviour as the hashtag chips
 * below, for the fields where "show me everything else like this" makes sense.
 */
function Field({
  label,
  value: raw,
  searchKey,
  small,
}: {
  label: string;
  value: string | undefined;
  searchKey?: 'brand' | 'productTitle' | 'imageType';
  /** Sets the value in the smaller size — for prose long enough to dominate
   *  the panel at the same size as the one-line fields above it. */
  small?: boolean;
}) {
  // The tagger's placeholders are stored strings, so an untagged brand arrived
  // here as the literal "{None}" and rendered as if it were a real value. The
  // row is dropped entirely instead — and the cleaned text is what shows, so a
  // decorated "{Pack Shot}" reads as "Pack Shot".
  const value = tagText(raw);
  if (!value) return null;
  // Label beside the value rather than stacked above it: as its own row each
  // label cost a line to say one word, and five fields spent five lines on
  // labels alone in the tallest panel on the page. The fixed 5.5rem column
  // keeps the values aligned into a readable second column — wide enough for
  // "Image type" and "Description" without wrapping them.
  return (
    <div className="flex gap-3">
      <dt className="w-[5.5rem] shrink-0 text-[11px] leading-5 text-faint">{label}</dt>
      <dd className={cn('min-w-0 flex-1 break-words text-body', small && 'text-[13px] leading-5')}>
        {searchKey ? (
          <Link
            to="/search"
            search={{ [searchKey]: value }}
            target="_blank"
            rel="noreferrer"
            title={t('assetDetailPage.searchFor', { value })}
            className="hover:text-brand-600 hover:underline dark:hover:text-brand-400"
          >
            {value}
          </Link>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

/** How many chips show before the rest collapse behind "+N". */
const HASHTAG_LIMIT = 6;

/**
 * The hashtag chips, capped at {@link HASHTAG_LIMIT} with a "+N" chip that
 * reveals the rest.
 *
 * A generous tagger returns a dozen or more hashtags, and at three rows deep
 * they outweighed every other field in the panel. Six covers the common case
 * in one or two rows; the overflow is one click away rather than gone.
 */
function HashtagList({ tags }: { tags: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const hidden = tags.length - HASHTAG_LIMIT;
  const shown = expanded || hidden <= 0 ? tags : tags.slice(0, HASHTAG_LIMIT);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((h) => (
        <HashtagLink key={h} tag={h} />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? t('assetDetailPage.showFewerHashtags') : hidden === 1 ? t('assetDetailPage.oneMoreHashtag', { n: hidden }) : t('assetDetailPage.nMoreHashtags', { n: hidden })}
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
            // surface-* / text-* are the theme utilities from styles.css: they
            // read a variable that the .dark block redefines, so no dark:
            // variant is needed here.
            'surface-sunken text-muted hover:surface-hover hover:text-body',
          )}
        >
          {expanded ? t('assetDetailPage.showLess') : `+${hidden}`}
        </button>
      )}
    </div>
  );
}

/**
 * A hashtag chip that runs an AI search for that tag in a new tab.
 *
 * The leading '#' is stripped from the search value: SearchPage's hashtag box
 * does the same on input (it matches stored tags, which have no '#'), so
 * sending it raw would search for a hashtag that no asset carries. The '#'
 * stays in the visible label.
 *
 * rel="noreferrer" is required alongside target="_blank" — without it the new
 * tab gets a window.opener handle back to this one.
 */
function HashtagLink({ tag }: { tag: string }) {
  const value = tag.replace(/^#/, '');
  if (!value) return null;
  return (
    <Link
      to="/search"
      search={{ hashtag: value }}
      target="_blank"
      rel="noreferrer"
      title={t('assetDetailPage.searchFor', { value: tag })}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
        'bg-brand-50 text-brand-800 hover:bg-brand-100 dark:bg-brand-900/40 dark:text-brand-100 dark:hover:bg-brand-900/70',
      )}
    >
      {tag}
    </Link>
  );
}

/**
 * Editable form over the five AI tag fields.
 *
 * Correcting a wrong value or adding a missing hashtag is the real
 * human-in-the-loop step, which is why this replaced the old bare Confirm
 * button. When the backend is read-only we still show the form (so the fields
 * are reviewable) but disable saving and say why, rather than letting a click
 * fail silently.
 */
function TagEditor({
  tags,
  readOnly,
  saving,
  error,
  onSave,
  onCancel,
}: {
  tags: TagFields;
  readOnly: boolean;
  saving: boolean;
  error: Error | null;
  onSave: (next: TagFields) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<TagFields>(tags);
  const set = (k: keyof TagFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <form
      className="space-y-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (!readOnly) onSave(draft);
      }}
    >
      <EditField label={t('assetDetailPage.brand')} value={draft.brand} onChange={set('brand')} />
      <EditField label={t('assetDetailPage.product')} value={draft.productTitle} onChange={set('productTitle')} />
      <EditField label={t('assetDetailPage.imageType')} value={draft.imageType} onChange={set('imageType')} />
      <EditField label={t('assetDetailPage.description')} value={draft.description} onChange={set('description')} multiline />
      <EditField
        label={t('assetDetailPage.hashtags')}
        value={draft.hashtags}
        onChange={set('hashtags')}
        hint={t('assetDetailPage.hashtagsHint')}
      />

      {error && <p className="text-xs text-red-600">{error.message}</p>}
      {readOnly && (
        <p className="text-xs text-faint">
          {t('assetDetailPage.savingDisabled')}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <Button type="submit" variant="primary" className="h-8 text-xs" loading={saving} disabled={readOnly}>
          {t('assetDetailPage.saveTags')}
        </Button>
        <Button type="button" variant="secondary" className="h-8 text-xs" onClick={onCancel}>
          {t('assetDetailPage.cancel')}
        </Button>
      </div>
    </form>
  );
}

function EditField({
  label,
  value,
  onChange,
  multiline,
  hint,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  multiline?: boolean;
  hint?: string;
}) {
  const cls =
    'mt-1 w-full rounded-md border border-line bg-[var(--surface-card)] px-2 py-1.5 text-sm text-body outline-none focus:border-brand-500';
  return (
    <label className="block">
      <span className="text-[11px] text-faint">{label}</span>
      {multiline ? (
        <textarea className={cls} rows={3} value={value} onChange={onChange} />
      ) : (
        <input className={cls} value={value} onChange={onChange} />
      )}
      {hint && <span className="mt-0.5 block text-[10px] text-faint">{hint}</span>}
    </label>
  );
}
